import { X, Trash2, Minus, Plus, Save, AlertCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function EditQuantityDrawer({ 
  isOpen, 
  itemName, 
  currentQuantity,
  currentCategory,
  categories,
  onSave, 
  onDelete, 
  onClose 
}) {
  const [name, setName] = useState(itemName);
  const [quantity, setQuantity] = useState(currentQuantity);
  const [selectedCategory, setSelectedCategory] = useState(currentCategory);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);

  // Reset quantity and category when drawer opens with new item
  useEffect(() => {
    if (isOpen) {
      setName(itemName);
      setQuantity(currentQuantity);
      setSelectedCategory(currentCategory);
      setShowDeleteConfirm(false);
      setIsCustomCategory(false);
      setCustomCategory('');
      setErrors({});
    }
  }, [isOpen, itemName, currentQuantity, currentCategory]);

  if (!isOpen) return null;

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleIncrement = () => {
    // Try to parse the number from the quantity string
    const match = quantity.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[2];
      setQuantity(`${num + 1}${unit ? ' ' + unit : ''}`);
    } else {
      // If no number found, just add 1
      setQuantity('1');
    }
  };

  const handleDecrement = () => {
    const match = quantity.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[2];
      if (num > 1) {
        setQuantity(`${num - 1}${unit ? ' ' + unit : ''}`);
      }
    }
  };

  const handleSave = () => {
    const nextName = (name || '').trim();
    const nextQty = (quantity || '').trim();
    const nextCustomCategory = customCategory.trim();
    const nextErrors = {};

    if (!nextName) nextErrors.name = 'Item name is required.';
    if (!nextQty) nextErrors.quantity = 'Quantity is required.';
    if (isCustomCategory && !nextCustomCategory) nextErrors.customCategory = 'Category name is required.';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      focusSummary();
      return;
    }

    setErrors({});
    onSave(nextName, nextQty, isCustomCategory ? nextCustomCategory : selectedCategory);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
    onClose();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 animate-slide-up max-w-[600px] mx-auto shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">
        <div className="p-6">
          {/* Title */}
          <h2 className="text-center mb-2">Edit Quantity</h2>

          {Object.keys(errors).length > 0 && (
            <div
              ref={summaryRef}
              tabIndex={-1}
              role="alert"
              aria-live="assertive"
              className="mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A] text-left"
            >
              <div className="flex items-start gap-2 text-[#B85A5A]">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium">Please fix the highlighted fields below.</p>
                  {errors.name && <p>{errors.name}</p>}
                  {errors.quantity && <p>{errors.quantity}</p>}
                  {errors.customCategory && <p>{errors.customCategory}</p>}
                </div>
              </div>
            </div>
          )}
          
          {/* Item Name */}
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError('name');
            }}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'shopping-item-name-error' : undefined}
            className={`w-full text-center py-3 px-4 border-2 border-[var(--border)] rounded-xl bg-input-background text-foreground focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)] transition-colors mb-4 ${errors.name ? 'ring-2 ring-[#B85A5A]' : ''}`}
            placeholder="Item name"
          />
          {errors.name && (
            <div id="shopping-item-name-error" className="mb-4 flex items-center gap-1.5 text-sm text-[#B85A5A]">
              <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
              <span>{errors.name}</span>
            </div>
          )}
          
          {/* Quantity Controls */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleDecrement}
              className="w-12 h-12 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center hover:bg-[var(--primary-light)] active:bg-[var(--primary-light)] transition-colors"
              aria-label="Decrease quantity"
            >
              <Minus className="w-5 h-5" />
            </button>
            
            <input
              type="text"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                clearError('quantity');
              }}
              aria-invalid={!!errors.quantity}
              aria-describedby={errors.quantity ? 'shopping-quantity-error' : undefined}
              className={`flex-1 text-center py-3 px-4 border-2 border-[var(--border)] rounded-xl bg-input-background text-foreground focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)] transition-colors ${errors.quantity ? 'ring-2 ring-[#B85A5A]' : ''}`}
              placeholder="e.g., 2, 500g, 1 bunch"
            />
            
            <button
              onClick={handleIncrement}
              className="w-12 h-12 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center hover:bg-[var(--primary-light)] active:bg-[var(--primary-light)] transition-colors"
              aria-label="Increase quantity"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          {errors.quantity && (
            <div id="shopping-quantity-error" className="mb-6 flex items-center gap-1.5 text-sm text-[#B85A5A]">
              <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
              <span>{errors.quantity}</span>
            </div>
          )}
          
          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-foreground mb-2">Category</label>
            {!isCustomCategory ? (
              <>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-input-background rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none text-foreground"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsCustomCategory(true)}
                  className="mt-2 text-sm flex items-center gap-1 text-[var(--primary-foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add New Category</span>
                </button>
              </>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => {
                    setCustomCategory(e.target.value);
                    clearError('customCategory');
                  }}
                  placeholder="Enter category name"
                  aria-invalid={!!errors.customCategory}
                  aria-describedby={errors.customCategory ? 'shopping-custom-category-error' : undefined}
                  className={`w-full px-4 py-3 bg-input-background rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] outline-none text-foreground ${errors.customCategory ? 'ring-2 ring-[#B85A5A]' : ''}`}
                />
                {errors.customCategory && (
                  <div id="shopping-custom-category-error" className="flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                    <span>{errors.customCategory}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomCategory(false);
                    setCustomCategory('');
                    clearError('customCategory');
                  }}
                  className="text-sm text-[var(--primary-foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  Use existing category
                </button>
              </div>
            )}
          </div>
          
          {/* Buttons */}
          <div className="space-y-4">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-light)] transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-hover)] transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
            <button
              onClick={handleDelete}
              className="w-full py-3 rounded-full bg-[var(--destructive)] text-[var(--destructive-foreground)] hover:bg-[color-mix(in_srgb,_var(--destructive)_90%,_#000_10%)] transition-colors flex items-center justify-center gap-2"
            >
              <Trash2
                className="w-4 h-4"
                style={{ color: 'var(--destructive-foreground)', stroke: 'var(--destructive-foreground)' }}
              />
              Delete Item
            </button>
          </div>
        </div>
        
        {/* Safe area spacing for mobile */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-50" onClick={handleCancelDelete} />
          
          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 animate-slide-up max-w-[600px] mx-auto shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">
            <div className="p-6">
              <h2 className="text-center mb-2">Delete Item?</h2>
              <p className="text-center text-muted-foreground mb-6">
                This will remove "{itemName}" from your shopping list.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-light)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 py-3 rounded-full bg-[var(--destructive)] text-[var(--destructive-foreground)] hover:bg-[color-mix(in_srgb,_var(--destructive)_90%,_#000_10%)] transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
            
            {/* Safe area spacing for mobile */}
            <div className="h-[env(safe-area-inset-bottom)]" />
          </div>
        </>
      )}
    </>
  );
}
